import { z } from 'zod'
import { PlanServiceAreaOverrideRuleStatus } from '../enums/plan-service-area-override-rule.enum'

// Outside the Zod schema — view-only, not editable.
export interface ServiceAreaViewMetadata {
  status: PlanServiceAreaOverrideRuleStatus
  createdAt: string
  createdByEmail: string | null
  updatedAt: string
  updatedByEmail: string | null
}

export const PlanServiceAreaOverrideFormSchema = z.object({
  year: z.coerce
    .number({ required_error: 'Year is required' })
    .int()
    .min(2020, 'Year must be 2020 or later')
    .max(2035, 'Year must be 2035 or earlier'),
  state: z.string().min(1, 'State is required'),
  issuerId: z
    .string()
    .min(1, 'Issuer ID is required')
    .length(5, 'Issuer ID must be exactly 5 digits')
    .regex(/^\d{5}$/, 'Issuer ID must be exactly 5 digits'),
  serviceAreaId: z.string().min(1, 'Service Area ID is required'),
  hiosPlanId: z.string().min(1, 'HIOS Plan ID is required'),
  countyCode: z.string().min(1, 'County Code is required'),
  zipcode: z
    .string()
    .length(5, 'Zipcode must be exactly 5 digits')
    .regex(/^\d{5}$/, 'Zipcode must be exactly 5 digits'),
  sourceName: z.string().min(1, 'Source Name is required'),
  serviceAreaName: z.string().min(1, 'Service Area Name is required'),
  coverEntireState: z.boolean({
    required_error: 'Cover Entire State is required',
  }),
  partialCounty: z.boolean({ required_error: 'Partial County is required' }),
  isActive: z.boolean({ required_error: 'Is Active is required' }),
})

export type PlanServiceAreaOverrideFormValues = z.infer<
  typeof PlanServiceAreaOverrideFormSchema
>

export function flatToApiPayload(values: PlanServiceAreaOverrideFormValues) {
  return {
    template: 'UPSERT_RULE' as const,
    match: {
      year: values.year,
      state: values.state,
      issuerId: values.issuerId,
      serviceAreaId: values.serviceAreaId,
      hiosPlanId: values.hiosPlanId,
      countyCode: values.countyCode,
      zipcode: values.zipcode,
    },
    params: {
      sourceName: values.sourceName,
      serviceAreaName: values.serviceAreaName,
      coverEntireState: values.coverEntireState,
      partialCounty: values.partialCounty,
      isActive: values.isActive,
    },
  }
}

export function apiPayloadToForm(rule: {
  match: {
    year: number
    state: string
    issuerId: string
    serviceAreaId: string
    hiosPlanId: string
    countyCode: string
    zipcode: string
  }
  params: {
    sourceName: string
    serviceAreaName: string
    coverEntireState: boolean
    partialCounty: boolean
    isActive: boolean
  }
}): PlanServiceAreaOverrideFormValues {
  return {
    year: rule.match.year,
    state: rule.match.state,
    issuerId: rule.match.issuerId,
    serviceAreaId: rule.match.serviceAreaId,
    hiosPlanId: rule.match.hiosPlanId,
    countyCode: rule.match.countyCode,
    zipcode: rule.match.zipcode,
    sourceName: rule.params.sourceName,
    serviceAreaName: rule.params.serviceAreaName,
    coverEntireState: rule.params.coverEntireState,
    partialCounty: rule.params.partialCounty,
    isActive: rule.params.isActive,
  }
}
